import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Mail, User, Lock, Gift, Eye, EyeOff } from 'lucide-react';
import { Logo } from './Logo';
import { checkUsernameAvailability, signUp, signIn } from '../lib/auth/authService';
import { supabase } from '../lib/supabase';

interface AuthProps {
  onClose?: () => void;
}

type Step = 'signin' | 'signup' | 'forgot';

export function Auth({ onClose }: AuthProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [mode, setMode] = useState<Step>('signin');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  
  const modalRef = useRef<HTMLDivElement>(null);

  // Remove the click-outside handler since we're handling it with onClick on the backdrop
  // This prevents conflicts with nested event handlers

  const validateForm = () => {
    // Use the correct email field based on mode
    const emailToValidate = mode === 'forgot' ? resetEmail : email;

    if (!emailToValidate.trim()) {
      setError('Please enter your email address');
      return false;
    }

    if (mode !== 'forgot' && !password.trim()) {
      setError('Please enter your password');
      return false;
    }

    if (mode === 'signup' && !username.trim()) {
      setError('Please choose a username');
      return false;
    }

    if (mode !== 'forgot' && password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToValidate)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (mode === 'signup') {
      const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
      if (!usernameRegex.test(username)) {
        setError('Username must be 3-20 characters long and can only contain letters, numbers, and underscores');
        return false;
      }
    }


    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    
    try {
      if (mode === 'signup') {
        const result = await signUp(email, password, username, 'member', referralCode || undefined);
        
        // Show warning if referral code was invalid
        if (result?.referralApplied === false && referralCode?.trim()) {
          // Still proceed — but user will see they need to check the code next time
          console.warn('Referral code was not valid');
        }
        
        // Redirect: if user came from claim page, send them back to complete the claim
        if (result?.user) {
          onClose?.();
          const isClaimFlow = /^\/claim\/[^/]+$/.test(location.pathname);
          navigate(isClaimFlow ? location.pathname : '/onboarding');
        } else {
          onClose?.();
        }
      } else if (mode === 'signin') {
        await signIn(email, password);
        onClose?.();
      } else if (mode === 'forgot') {
        // First check if account exists with this email
        const { data: existingUser } = await supabase
          .from('users')
          .select('id')
          .eq('email', resetEmail.toLowerCase())
          .single();

        if (!existingUser) {
          throw new Error('No account found with this email address');
        }

        const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (resetError) throw resetError;
        setResetSent(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] md:z-50"
        onClick={(e) => {
          // Close when clicking on the backdrop
          if (e.target === e.currentTarget && onClose) {
            onClose();
          }
        }}
      >
      <div 
        ref={modalRef} 
        className="w-full h-full md:h-auto md:max-w-5xl bg-background md:rounded-lg overflow-hidden flex flex-col md:flex-row shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={() => onClose?.()}
          className="absolute top-4 right-4 p-2 text-content/60 hover:text-content rounded-full hover:bg-accent-base/10 z-10 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Left Side - Form */}
        <div className="w-full md:w-1/2 p-6 md:p-8 lg:p-12 flex flex-col overflow-y-auto">
          {/* Logo */}
          <div className="mb-8 md:mb-12">
            <div className="flex items-center space-x-2">
              <Logo className="h-16 w-16 text-accent-text" />
              <span className="text-2xl font-gelica font-bold">Mindful Family</span>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 max-w-md mx-auto w-full flex flex-col justify-start md:justify-center py-4 md:py-0 min-h-0">
            <h1 className="text-4xl md:text-5xl font-gelica font-bold mb-4">
              {mode === 'signin' ? t('auth.welcomeBack') : mode === 'signup' ? t('auth.getStarted') : t('auth.resetPassword')}
            </h1>
            <p className="text-content/60 mb-8">
              {mode === 'forgot' ? (
                t('auth.resetPasswordDesc')
              ) : (
                <>
                  {mode === 'signin' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
                  <button 
                    onClick={() => {
                      setMode(mode === 'signin' ? 'signup' : 'signin');
                      setError('');
                    }}
                    className="text-accent-text hover:text-accent-text/80"
                  >
                    {mode === 'signin' ? t('auth.signUp') : t('auth.signIn')}
                  </button>
                </>
              )}
            </p>

            <form onSubmit={handleAuth} className="space-y-6 pb-8 md:pb-0">
              {mode === 'signup' && (
                <div>
                  <label htmlFor="auth-username" className="block text-sm font-medium text-content mb-1.5">{t('auth.username')}</label>
                  <div className="relative">
                    <input
                      id="auth-username"
                      type="text"
                      required
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        setError('');
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-background border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                      placeholder={t('auth.chooseUsername')}
                    />
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                  </div>
                </div>
              )}

              {mode === 'forgot' ? (
                <div>
                  <label htmlFor="auth-reset-email" className="block text-sm font-medium text-content mb-1.5">{t('auth.email')}</label>
                  <div className="relative">
                    <input
                      id="auth-reset-email"
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => {
                        setResetEmail(e.target.value);
                        setError('');
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-background border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                      placeholder={t('auth.enterEmail')}
                    />
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="auth-email" className="block text-sm font-medium text-content mb-1.5">{t('auth.email')}</label>
                    <div className="relative">
                      <input
                        id="auth-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        className="w-full pl-11 pr-4 py-3 bg-background border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                        placeholder={t('auth.enterEmail')}
                      />
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="auth-password" className="block text-sm font-medium text-content mb-1.5">{t('auth.password')}</label>
                    <div className="relative">
                      <input
                        id="auth-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError('');
                        }}
                        className="w-full pl-11 pr-11 py-3 bg-background border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                        placeholder={t('auth.enterPassword')}
                      />
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-content/40 hover:text-content/60 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {mode === 'signup' && (
                <div>
                  <label htmlFor="auth-referral" className="block text-sm font-medium text-content mb-1.5">
                    {t('auth.referralCode')} <span className="text-content/40 font-normal">({t('common.optional')})</span>
                  </label>
                  <div className="relative">
                    <input
                      id="auth-referral"
                      type="text"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value.toUpperCase());
                        setError('');
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-background border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                      placeholder={t('auth.referralCodePlaceholder')}
                    />
                    <Gift className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                  </div>
                </div>
              )}

              {mode === 'signup' && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-content/80 leading-relaxed">
                    {t('auth.agreeToTerms')}{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-accent-text hover:text-accent-text/80 font-medium underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.termsOfService')}
                    </Link>
                    {' '}{t('auth.and')}{' '}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      className="text-accent-text hover:text-accent-text/80 font-medium underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.privacyPolicy')}
                    </Link>
                    .
                  </p>
                </div>
              )}

              {resetSent && mode === 'forgot' && (
                <div className="bg-green-50 text-green-600 p-4 rounded-lg text-sm">
                  {t('auth.resetEmailSent')}
                </div>
              )}

              {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent-text text-white py-3 rounded-lg font-medium hover:bg-accent-text/90 transition-colors disabled:opacity-50"
                >
                  {loading ? t('common.loading') : mode === 'signin' ? t('auth.signIn') : mode === 'signup' ? t('auth.signUp') : t('auth.resetPassword')}
                </button>

                {mode === 'forgot' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setError('');
                      setResetSent(false);
                    }}
                    className="w-full text-center text-accent-text hover:text-accent-text/80"
                  >
                    {t('auth.backToSignIn')}
                  </button>
                ) : mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    className="w-full text-center text-accent-text hover:text-accent-text/80"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Image Only */}
        <div
          className="hidden md:block md:w-1/2 relative overflow-hidden"
          style={{
            backgroundImage: 'url("https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/humans-connecting.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      </div>
      </div>
    </>
  );
}

export default Auth